/**
 * Gift Engine controller — storefront side.
 *
 * The cart sections (mini-cart.liquid) render a server-computed state blob:
 *   <script type="application/json" data-gift-engine-state>
 *   {"triggerCents": 7000, "requiredGifts": "123,456", "giftLines": "123"}
 *   </script>
 * Liquid has the collection data, JS does not — so the trigger sum and the
 * matching rules are computed server-side on every section re-render and this
 * controller only reconciles differences, as a set (concurrent campaigns can
 * require several gifts at once):
 *
 *   - gift line present that is not the required tier -> remove it
 *   - required tier missing from the cart            -> add it
 *   - customer removed a gift via its remove button  -> opt-out, don't re-add
 *     while the cart still qualifies (localStorage, cleared when the cart
 *     stops requiring that gift)
 *
 * Mutations go through window.GiftEngineCart (bridge in cart.js), which shares
 * the serialized cart mutation queue, so gift changes never race user edits.
 * Each mutation's response re-renders the sections (fresh blob) and fires
 * cart:updated again, so reconcile() converges to a no-op.
 */
(function () {
  // Re-entrancy/cooldown guard: if a mutation fails (e.g. gift out of stock),
  // the error path refreshes the cart, which fires cart:updated again. Without
  // a cooldown the same failing op would be retried in a tight loop.
  var recentAttempts = new Map();
  var RETRY_COOLDOWN_MS = 5000;

  // Marketing request: customers may remove a gift themselves. Without memory
  // the reconcile loop would re-add it while the rule still matches — so a
  // manual removal records an opt-out for that variant, cleared automatically
  // once the cart stops requiring that gift (tier change / below threshold).
  var OPT_OUT_KEY = 'gift-engine-opt-out';

  function getOptOuts() {
    try {
      var parsed = JSON.parse(localStorage.getItem(OPT_OUT_KEY) || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  function setOptOuts(list) {
    try {
      localStorage.setItem(OPT_OUT_KEY, JSON.stringify(list));
    } catch (error) {}
  }

  function readState() {
    var el = document.querySelector('script[data-gift-engine-state]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      return null;
    }
  }

  function reconcile() {
    if (!window.GiftEngineCart) return;
    var state = readState();
    if (!state) return;

    // "requiredGifts" is an array of {id, rule} — the rule name gets stamped
    // onto the gift line as _gift_rule (a signal for downstream auditing).
    // Legacy blobs carried a comma-separated id string ("requiredGift" is the
    // even older single-gift shape) — both kept for cached pages/CDN mixes.
    var requiredEntries = [];
    if (Array.isArray(state.requiredGifts)) {
      requiredEntries = state.requiredGifts;
    } else {
      var legacyRaw = state.requiredGifts !== undefined ? state.requiredGifts : (state.requiredGift || '');
      requiredEntries = String(legacyRaw).split(',').filter(Boolean).map(function (id) {
        return { id: id, rule: 'gift-engine' };
      });
    }
    var required = requiredEntries.map(function (entry) { return String(entry.id); });
    var ruleByVariant = {};
    requiredEntries.forEach(function (entry) { ruleByVariant[String(entry.id)] = entry.rule || 'gift-engine'; });
    var present = (state.giftLines || '').split(',').filter(Boolean);
    // Line keys aligned with `present`, for key-targeted removal (a customer
    // may hold a normal, full-price line of the same variant)
    var presentKeys = (state.giftLineKeys || '').split(',');
    var now = Date.now();

    // Opt-outs: skip re-adding gifts the customer removed; drop stale opt-outs
    // for gifts the cart no longer requires so future qualification re-adds them
    var optOuts = getOptOuts();
    var stillRelevant = optOuts.filter(function (id) { return required.indexOf(id) !== -1; });
    if (stillRelevant.length !== optOuts.length) setOptOuts(stillRelevant);

    // Remove gift lines that should not be in the cart (rule no longer matches)
    present.forEach(function (variantId, index) {
      if (required.indexOf(variantId) === -1) enqueue(variantId, 0, now, presentKeys[index]);
    });

    // Add every required gift that is missing and not opted out of
    required.forEach(function (variantId) {
      if (present.indexOf(variantId) === -1 && stillRelevant.indexOf(variantId) === -1) {
        enqueue(variantId, 1, now, undefined, ruleByVariant[variantId]);
      }
    });
  }

  function enqueue(variantId, quantity, now, lineKey, ruleName) {
    var opKey = variantId + ':' + quantity;
    var lastAttempt = recentAttempts.get(opKey) || 0;
    if (now - lastAttempt < RETRY_COOLDOWN_MS) return;
    recentAttempts.set(opKey, now);
    window.GiftEngineCart.enqueue(variantId, quantity, lineKey, ruleName);
  }

  var scheduled = false;
  function scheduleReconcile() {
    if (scheduled) return;
    scheduled = true;
    // Defer so the DOM swap from the current event has fully settled
    setTimeout(function () {
      scheduled = false;
      reconcile();
    }, 50);
  }

  document.addEventListener('cart:updated', scheduleReconcile);

  // Gift remove buttons (data-gift-remove): capture phase so the generic
  // cart-remove-button handler (line-index based, can't resolve gift lines)
  // never fires. Records the opt-out, then removes the line by key.
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-gift-remove]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    if (!window.GiftEngineCart) return;

    var variantId = String(button.getAttribute('data-gift-remove'));
    var lineKey = button.getAttribute('data-key') || null;
    var optOuts = getOptOuts();
    if (optOuts.indexOf(variantId) === -1) {
      optOuts.push(variantId);
      setOptOuts(optOuts);
    }
    window.GiftEngineCart.enqueue(variantId, 0, lineKey);
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    reconcile();
  }
})();
